import { roundMoney, type PaidPartialInvoice } from './finalInvoiceTotals';

export type DeductionInvoiceRow = {
  id: string;
  document_type: string;
  invoice_number?: string | null;
  reference_id?: string | null;
  issue_date: string | null;
  status?: string | null;
  net_amount?: number | null;
  tax_amount?: number | null;
  gross_amount?: number | null;
  retention_gross?: number | null;
  retention_status?: string | null;
  lead_display_id?: string | null;
  lead_title?: string | null;
  site_address?: string | null;
  offering_display_id?: string | null;
  offering_id?: string | null;
};

const DISPLAY_DOCUMENT_TYPES = new Set(['invoice', 'partial_invoice', 'final_invoice', 'credit_note', 'invoice_correction']);

const DOCUMENT_TYPE_LABEL_DE: Record<string, string> = {
  invoice: 'Rechnung',
  partial_invoice: 'Abschlagsrechnung',
  final_invoice: 'Schlussrechnung',
  credit_note: 'Stornorechnung',
  invoice_correction: 'Gutschrift',
};

export function documentTypeLabelDe(documentType: string): string {
  return DOCUMENT_TYPE_LABEL_DE[documentType] ?? documentType;
}

export function isDeductionDisplayRow(row: Pick<DeductionInvoiceRow, 'document_type'>): boolean {
  return DISPLAY_DOCUMENT_TYPES.has(String(row.document_type ?? '').trim());
}

/** Issued Abschläge that may be summarized / deducted (not drafts, cancelled, etc.). */
const ISSUED_PARTIAL_INVOICE_STATUSES = new Set(['open', 'paid', 'overdue']);

export function isSelectableDeductionRow(row: Pick<DeductionInvoiceRow, 'document_type' | 'status'>): boolean {
  const docType = String(row.document_type ?? '').trim();
  const status = String(row.status ?? '').trim().toLowerCase();
  return docType === 'partial_invoice' && ISSUED_PARTIAL_INVOICE_STATUSES.has(status);
}

export function selectableDeductionDisabledReason(
  row: Pick<DeductionInvoiceRow, 'document_type' | 'status'>,
): string | null {
  if (isSelectableDeductionRow(row)) return null;
  const docType = String(row.document_type ?? '').trim();
  if (docType !== 'partial_invoice') return 'Nur Abschlagsrechnungen können abgezogen werden.';
  const status = String(row.status ?? '').trim().toLowerCase();
  if (status === 'cancelled') return 'Stornierte Rechnung.';
  if (!ISSUED_PARTIAL_INVOICE_STATUSES.has(status)) {
    return 'Nur gestellte Abschlagsrechnungen können abgezogen werden.';
  }
  return 'Nicht auswählbar.';
}

export function mapInvoiceRowToDeductionCandidate(
  row: DeductionInvoiceRow,
  fallbackVatRatePercent: number,
): PaidPartialInvoice {
  const netRaw = Number(row.net_amount ?? Number.NaN);
  const vatRaw = Number(row.tax_amount ?? Number.NaN);
  const grossRaw = Number(row.gross_amount ?? Number.NaN);
  const hasGross = Number.isFinite(grossRaw);
  const gross = hasGross ? grossRaw : 0;

  let net = Number.isFinite(netRaw) ? netRaw : Number.NaN;
  let vat = Number.isFinite(vatRaw) ? vatRaw : Number.NaN;

  if ((!Number.isFinite(net) || !Number.isFinite(vat)) && hasGross) {
    if (fallbackVatRatePercent > 0) {
      net = Math.round((gross / (1 + fallbackVatRatePercent / 100)) * 100) / 100;
      vat = Math.round((gross - net) * 100) / 100;
    } else {
      net = gross;
      vat = 0;
    }
  }

  if (Number.isFinite(net) && !Number.isFinite(vat) && hasGross) {
    vat = Math.round((gross - net) * 100) / 100;
  }
  if (!Number.isFinite(net)) net = 0;
  if (!Number.isFinite(vat)) vat = 0;

  return {
    id: String(row.id),
    referenceId: String(row.invoice_number || row.reference_id || row.id),
    issueDate: row.issue_date,
    status: String(row.status ?? ''),
    netAmount: roundMoney(net),
    vatAmount: roundMoney(vat),
    grossAmount: roundMoney(gross),
    retentionGross: roundMoney(Math.max(0, Number(row.retention_gross) || 0)),
  };
}

export function derivePaidPartialInvoices(
  candidates: PaidPartialInvoice[],
  selectedIds: string[],
): PaidPartialInvoice[] {
  const idSet = new Set(selectedIds);
  return candidates.filter((c) => idSet.has(c.id));
}

export type CurrentDeductionDocument = {
  ids?: Iterable<string>;
  referenceIds?: Iterable<string>;
};

/** Last issued invoice for this document type, stored on the offering draft. */
export type BoundIssuedInvoice = {
  id?: string | null;
  referenceId?: string | null;
};

export function parseBoundIssuedInvoice(raw: unknown): BoundIssuedInvoice | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const id = typeof o.id === 'string' ? o.id.trim() : '';
  const referenceId = typeof o.referenceId === 'string' ? o.referenceId.trim() : '';
  if (!id && !referenceId) return null;
  return {
    ...(id ? { id } : {}),
    ...(referenceId ? { referenceId } : {}),
  };
}

export function parseCurrentIssuedInvoiceByDocType(
  raw: unknown,
): Record<string, BoundIssuedInvoice> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: Record<string, BoundIssuedInvoice> = {};
  for (const [label, value] of Object.entries(raw as Record<string, unknown>)) {
    const key = String(label ?? '').trim();
    const parsed = parseBoundIssuedInvoice(value);
    if (!key || !parsed) continue;
    out[key] = parsed;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Current document for “Bisherige Abschläge”:
 * - Reload without a new PDF: still bound to the last issued invoice of this type.
 * - PDF/allocate with a *new* number: that invoice is now a prior Abschlag.
 */
export function resolveCurrentDeductionDocument(input: {
  boundIssued?: BoundIssuedInvoice | null;
  liveReferenceId?: string | null;
}): CurrentDeductionDocument {
  const live = String(input.liveReferenceId ?? '').trim();
  const boundId = String(input.boundIssued?.id ?? '').trim();
  const boundRef = String(input.boundIssued?.referenceId ?? '').trim();

  if (live && boundRef && normalizeDeductionKey(live) !== normalizeDeductionKey(boundRef)) {
    return { ids: [], referenceIds: [live] };
  }

  const ids = boundId ? [boundId] : [];
  const referenceIds = Array.from(new Set([live, boundRef].filter(Boolean)));
  return { ids, referenceIds };
}

function normalizeDeductionKey(value: string | null | undefined): string {
  return String(value ?? '').trim().toLowerCase();
}

function currentDeductionKeySets(current?: CurrentDeductionDocument | null): {
  ids: Set<string>;
  referenceIds: Set<string>;
} {
  const ids = new Set<string>();
  const referenceIds = new Set<string>();
  if (!current) return { ids, referenceIds };
  for (const id of current.ids ?? []) {
    const key = String(id ?? '').trim();
    if (key) ids.add(key);
  }
  for (const ref of current.referenceIds ?? []) {
    const key = normalizeDeductionKey(ref);
    if (key) referenceIds.add(key);
  }
  return { ids, referenceIds };
}

/** True when this invoice is the document currently being edited / reprinted. */
export function isCurrentDeductionDocument(
  invoice: { id?: string | null; referenceId?: string | null },
  current?: CurrentDeductionDocument | null,
): boolean {
  const { ids, referenceIds } = currentDeductionKeySets(current);
  const id = String(invoice.id ?? '').trim();
  if (id && ids.has(id)) return true;
  const referenceId = normalizeDeductionKey(invoice.referenceId);
  return Boolean(referenceId) && referenceIds.has(referenceId);
}

export function deductionRowMatchesCurrentDocument(
  row: Pick<DeductionInvoiceRow, 'id' | 'invoice_number' | 'reference_id'>,
  current?: CurrentDeductionDocument | null,
): boolean {
  return isCurrentDeductionDocument(
    {
      id: row.id,
      referenceId: String(row.invoice_number || row.reference_id || '').trim(),
    },
    current,
  );
}

/**
 * Explicitly selected invoices stay: the next Abschlag may deduct the one this draft issued last.
 * Every new PDF gets a new number, so a selected invoice is never the document being printed.
 */
export function excludeCurrentDocumentDeductions<T extends { id: string; referenceId: string }>(
  invoices: T[],
  current?: CurrentDeductionDocument | null,
  selectedIds?: Iterable<string>,
): T[] {
  if (!current) return invoices;
  const selected = new Set(selectedIds ?? []);
  return invoices.filter(
    (invoice) => selected.has(invoice.id) || !isCurrentDeductionDocument(invoice, current),
  );
}

export function resolveDeductionSelectionIds(
  rows: DeductionInvoiceRow[],
  parsedSelection: string[] | undefined,
  hasExplicitSelection: boolean,
  offeringId: string | null | undefined,
  currentDocument?: CurrentDeductionDocument | null,
): string[] {
  // An explicit pick may be the invoice this draft issued last; only the default skips it.
  if (hasExplicitSelection) {
    return filterValidSelectedDeductionIds(parsedSelection ?? [], rows);
  }
  const usableRows = currentDocument
    ? rows.filter((row) => !deductionRowMatchesCurrentDocument(row, currentDocument))
    : rows;
  return usableRows
    .filter(
      (row) =>
        isSelectableDeductionRow(row) &&
        (!offeringId || String(row.offering_id ?? '') === String(offeringId)),
    )
    .map((row) => String(row.id));
}

export function filterValidSelectedDeductionIds(
  selectedIds: string[],
  rows: DeductionInvoiceRow[],
): string[] {
  const selectableIds = new Set(
    rows.filter(isSelectableDeductionRow).map((r) => String(r.id)),
  );
  return selectedIds.filter((id) => selectableIds.has(id));
}
